/**
 * HierarchicalRoleAssignment - Assign users to organization units with specific roles
 * Supports the hierarchical RBAC system:
 * - Assign roles scoped to specific organization units
 * - View user's current role assignments
 * - Configure privacy settings per unit
 * - Manage hierarchical access levels
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import {
  Shield, Users, Building2, Plus, Trash2, Eye, EyeOff,
  Loader2, CheckCircle, ChevronRight, Crown, Lock,
  Settings, UserPlus, Search, AlertCircle, Check
} from 'lucide-react';
import api from '@/lib/api';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Role colors for visual distinction
const ROLE_COLORS = {
  super_admin: 'bg-purple-500',
  hq_admin: 'bg-indigo-500',
  province_admin: 'bg-blue-500',
  district_admin: 'bg-cyan-500',
  church_admin: 'bg-green-500',
  finance_officer: 'bg-yellow-500',
  member: 'bg-gray-500',
  viewer: 'bg-slate-400',
};

// Hierarchy level badges
const LEVEL_BADGES = {
  hq: { color: 'bg-purple-100 text-purple-700', label: 'HQ' },
  province: { color: 'bg-blue-100 text-blue-700', label: 'Province' },
  region: { color: 'bg-cyan-100 text-cyan-700', label: 'Region' },
  country: { color: 'bg-green-100 text-green-700', label: 'Country' },
  district: { color: 'bg-yellow-100 text-yellow-700', label: 'District' },
  campus: { color: 'bg-orange-100 text-orange-700', label: 'Campus' },
  assembly: { color: 'bg-pink-100 text-pink-700', label: 'Assembly' },
  home_group: { color: 'bg-gray-100 text-gray-700', label: 'Home Group' },
};

const HierarchicalRoleAssignment = () => {
  const [activeTab, setActiveTab] = useState('users');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showPrivacyDialog, setShowPrivacyDialog] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const queryClient = useQueryClient();

  // Fetch available roles
  const { data: roles = [] } = useQuery({
    queryKey: ['rbac-roles'],
    queryFn: async () => {
      const { data } = await api.get('/admin/rbac/roles');
      return data;
    }
  });

  // Fetch default role templates
  const { data: defaultRoles } = useQuery({
    queryKey: ['rbac-default-roles'],
    queryFn: async () => {
      const { data } = await api.get('/admin/rbac/default-roles');
      return data;
    }
  });

  // Fetch hierarchy levels
  const { data: hierarchyData } = useQuery({
    queryKey: ['hierarchy-levels'],
    queryFn: async () => {
      const { data } = await api.get('/admin/rbac/hierarchy-levels');
      return data;
    }
  });

  // Fetch permission modules
  const { data: permissionModules } = useQuery({
    queryKey: ['permission-modules'],
    queryFn: async () => {
      const { data } = await api.get('/admin/rbac/permission-modules');
      return data;
    }
  });

  // Fetch organization units
  const { data: orgUnits = [] } = useQuery({
    queryKey: ['org-units'],
    queryFn: async () => {
      const { data } = await api.get('/admin/org/units');
      return data;
    }
  });

  // Fetch team members
  const { data: teamMembers = [], isLoading: loadingMembers } = useQuery({
    queryKey: ['team-members'],
    queryFn: async () => {
      const { data } = await api.get('/admin/team');
      return data;
    }
  });

  // Fetch my permissions
  const { data: myPermissions } = useQuery({
    queryKey: ['my-permissions'],
    queryFn: async () => {
      const { data } = await api.get('/admin/rbac/my-permissions');
      return data;
    }
  });

  // Assign role mutation
  const assignRoleMutation = useMutation({
    mutationFn: async ({ userId, roleId, orgUnitId }) => {
      const { data } = await api.post(`/admin/rbac/users/${userId}/roles`, {
        user_id: userId, role_id: roleId, org_unit_id: orgUnitId
      });
      return data;
    },
    onSuccess: () => {
      toast.success('Role assigned successfully');
      queryClient.invalidateQueries(['team-members']);
      setShowAssignDialog(false);
    },
    onError: () => {
      toast.error('Failed to assign role');
    }
  });

  // Remove role mutation
  const removeRoleMutation = useMutation({
    mutationFn: async ({ userId, assignmentId }) => {
      const { data } = await api.delete(`/admin/rbac/users/${userId}/roles/${assignmentId}`);
      return data;
    },
    onSuccess: () => {
      toast.success('Role removed');
      queryClient.invalidateQueries(['team-members']);
    },
    onError: () => {
      toast.error('Failed to remove role');
    }
  });

  // Update privacy settings mutation
  const updatePrivacyMutation = useMutation({
    mutationFn: async ({ unitId, settings }) => {
      const { data } = await api.put(`/admin/rbac/privacy/${unitId}`, settings);
      return data;
    },
    onSuccess: () => {
      toast.success('Privacy settings updated');
      setShowPrivacyDialog(false);
    },
    onError: () => {
      toast.error('Failed to update privacy settings');
    }
  });

  // Filter users by search
  const teamMembersArray = Array.isArray(teamMembers) ? teamMembers : [];
  const orgUnitsArray = Array.isArray(orgUnits) ? orgUnits : [];
  const rolesArray = Array.isArray(roles) ? roles : [];
  
  const filteredUsers = teamMembersArray.filter(user => 
    user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.last_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Build org unit tree
  const buildOrgTree = (units, parentId = null) => {
    return units
      .filter(u => u.parent_id === parentId)
      .map(u => ({
        ...u,
        children: buildOrgTree(units, u.id)
      }));
  };
  const orgTree = buildOrgTree(orgUnitsArray);

  const canManageRoles = myPermissions?.role?.permissions?.users?.includes('manage');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Role & Permission Management</h1>
          <p className="text-slate-500">
            Assign roles to users with hierarchical access control
          </p>
        </div>
        {myPermissions && (
          <Badge variant="outline" className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Your Role: {(myPermissions.role?.name || 'Unknown').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
          </Badge>
        )}
      </div>

      {/* My Permissions Overview */}
      <Card className="bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-indigo-100 rounded-full">
                <Crown className="h-6 w-6 text-indigo-600" />
              </div>
              <div>
                <h3 className="font-semibold">Your Access Level</h3>
                <p className="text-sm text-slate-600">
                  {myPermissions?.can_see_all_levels 
                    ? 'You have full access to all organization units'
                    : `Access limited to ${myPermissions?.org_unit?.name || 'your assigned unit'}`}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              {myPermissions?.role?.permissions && Object.entries(myPermissions.role.permissions).slice(0, 4).map(([module, actions]) => (
                <Badge key={module} variant="secondary" className="text-xs">
                  {module}: {actions.length}
                </Badge>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="users">
            <Users className="h-4 w-4 mr-2" />
            User Assignments
          </TabsTrigger>
          <TabsTrigger value="roles">
            <Shield className="h-4 w-4 mr-2" />
            Available Roles
          </TabsTrigger>
          <TabsTrigger value="hierarchy">
            <Building2 className="h-4 w-4 mr-2" />
            Hierarchy & Privacy
          </TabsTrigger>
        </TabsList>

        {/* Users Tab */}
        <TabsContent value="users" className="space-y-4">
          {/* Search */}
          <div className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input 
                placeholder="Search users..." 
                className="pl-10"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="user-search"
              />
            </div>
            {canManageRoles && (
              <Button onClick={() => { setSelectedUser(null); setShowAssignDialog(true); }} data-testid="assign-role-btn">
                <UserPlus className="h-4 w-4 mr-2" />
                Assign Role
              </Button>
            )}
          </div>

          {/* User List */}
          <Card>
            <CardContent className="p-0">
              {loadingMembers ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Global Role</TableHead>
                      <TableHead>Organization Unit</TableHead>
                      <TableHead>Access Level</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUsers.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{user.first_name} {user.last_name}</div>
                            <div className="text-sm text-slate-500">{user.email}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={`${ROLE_COLORS[user.global_role] || 'bg-gray-500'} text-white`}>
                            {user.global_role?.replace(/_/g, ' ') || 'No Role'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {user.org_unit_name ? (
                            <div className="flex items-center gap-2">
                              <Building2 className="h-4 w-4 text-slate-400" />
                              {user.org_unit_name}
                            </div>
                          ) : (
                            <span className="text-slate-400">Not assigned</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {user.org_unit_level && (
                            <Badge className={LEVEL_BADGES[user.org_unit_level]?.color || 'bg-gray-100'}>
                              {LEVEL_BADGES[user.org_unit_level]?.label || user.org_unit_level}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            {canManageRoles && (
                              <>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => { setSelectedUser(user); setShowAssignDialog(true); }}
                                  data-testid={`edit-role-${user.id}`}
                                >
                                  <Shield className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredUsers.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                          No users found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Roles Tab */}
        <TabsContent value="roles" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* System Roles */}
            {defaultRoles && Object.entries(defaultRoles).map(([roleName, roleData]) => (
              <Card key={roleName} className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${ROLE_COLORS[roleName] || 'bg-gray-500'}`} />
                      {roleName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </CardTitle>
                    <Badge variant="outline" className="text-xs">
                      <Lock className="h-3 w-3 mr-1" />
                      System
                    </Badge>
                  </div>
                  <CardDescription>
                    Level: {roleData.level || 'Any'} | 
                    {roleData.can_see_all_levels ? ' Full Access' : ' Limited Access'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-32">
                    <div className="space-y-1">
                      {Object.entries(roleData.permissions || {}).map(([module, actions]) => (
                        <div key={module} className="flex items-center justify-between text-sm">
                          <span className="capitalize">{module}</span>
                          <Badge variant="secondary" className="text-xs">
                            {actions.length} actions
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Permission Modules Reference */}
          {permissionModules && (
            <Card>
              <CardHeader>
                <CardTitle>Permission Modules Reference</CardTitle>
                <CardDescription>Available permission modules and their actions</CardDescription>
              </CardHeader>
              <CardContent>
                <Accordion type="multiple" className="w-full">
                  {Object.entries(permissionModules.modules || {}).map(([moduleKey, moduleData]) => (
                    <AccordionItem key={moduleKey} value={moduleKey}>
                      <AccordionTrigger>
                        <div className="flex items-center gap-2">
                          <Shield className="h-4 w-4" />
                          <span className="font-medium">{moduleData.name}</span>
                          <Badge variant="outline" className="ml-2">{moduleData.actions?.length || 0} actions</Badge>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <p className="text-sm text-slate-500 mb-2">{moduleData.description}</p>
                        <div className="flex flex-wrap gap-2">
                          {moduleData.actions?.map((action) => (
                            <Badge key={action} variant="secondary">{action}</Badge>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Hierarchy & Privacy Tab */}
        <TabsContent value="hierarchy" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Hierarchy Levels */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Hierarchy Levels
                </CardTitle>
                <CardDescription>Organization structure from top to bottom</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {hierarchyData?.levels?.map((level, index) => (
                    <div 
                      key={level} 
                      className="flex items-center gap-3 p-3 rounded-lg bg-slate-50"
                      style={{ marginLeft: `${index * 16}px` }}
                    >
                      {index > 0 && <ChevronRight className="h-4 w-4 text-slate-400" />}
                      <Badge className={LEVEL_BADGES[level]?.color || 'bg-gray-100'}>
                        {LEVEL_BADGES[level]?.label || level}
                      </Badge>
                      <span className="text-sm text-slate-600">
                        {hierarchyData?.descriptions?.[level]}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Organization Units with Privacy */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Unit Privacy Settings
                </CardTitle>
                <CardDescription>Configure data visibility per unit</CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px]">
                  <div className="space-y-2">
                    {orgUnitsArray.map((unit) => (
                      <div 
                        key={unit.id}
                        className="flex items-center justify-between p-3 rounded-lg border hover:bg-slate-50"
                      >
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-slate-400" />
                          <div>
                            <div className="font-medium">{unit.name}</div>
                            <Badge className={LEVEL_BADGES[unit.level]?.color || 'bg-gray-100'} variant="outline">
                              {LEVEL_BADGES[unit.level]?.label || unit.level}
                            </Badge>
                          </div>
                        </div>
                        {canManageRoles && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => { setSelectedUnit(unit); setShowPrivacyDialog(true); }}
                            data-testid={`privacy-${unit.id}`}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            Privacy
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Assign Role Dialog */}
      <AssignRoleDialog 
        open={showAssignDialog}
        onClose={() => setShowAssignDialog(false)}
        user={selectedUser}
        users={teamMembersArray}
        roles={rolesArray}
        defaultRoles={defaultRoles}
        orgUnits={orgUnitsArray}
        onAssign={assignRoleMutation.mutate}
        isLoading={assignRoleMutation.isPending}
      />

      {/* Privacy Settings Dialog */}
      <PrivacySettingsDialog
        open={showPrivacyDialog}
        onClose={() => setShowPrivacyDialog(false)}
        unit={selectedUnit}
        onSave={updatePrivacyMutation.mutate}
        isLoading={updatePrivacyMutation.isPending}
      />
    </div>
  );
};

// Role preview — what this person can do now, and after (iter 611).
// Resolved server-side through the same code the guards use, so the screen
// cannot promise something a guard would then refuse.
const RolePreview = ({ userId, roleId }) => {
  const { data, isFetching } = useQuery({
    queryKey: ['role-preview', userId, roleId],
    queryFn: async () => {
      const { data } = await api.get('/admin/rbac/role-preview', {
        params: { user_id: userId, role_id: roleId },
      });
      return data;
    },
    enabled: !!userId && !!roleId,
    retry: false,
  });

  if (!userId || !roleId) return null;
  if (isFetching && !data) {
    return (
      <div className="flex items-center gap-2 rounded-lg border bg-slate-50 p-3 text-sm text-slate-500"
           data-testid="role-preview-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Working out what this grants…
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="space-y-3 rounded-lg border bg-slate-50 p-3" data-testid="role-preview">
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-2 text-sm font-semibold text-slate-700">
          <Eye className="h-4 w-4" /> What this grants
        </span>
        <Badge variant="outline" data-testid="role-preview-tier">
          {data.role?.tier || 'unranked'}
        </Badge>
      </div>

      {!data.allowed && (
        <p className="flex items-start gap-2 rounded-md bg-red-50 p-2 text-xs text-red-700"
           data-testid="role-preview-refusal">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /> {data.refusal}
        </p>
      )}

      <p className="text-xs text-slate-600" data-testid="role-preview-counts">
        Holds <strong>{data.current?.count ?? 0}</strong> permissions now ·
        gains <strong>{data.after?.added?.length ?? 0}</strong> ·
        total <strong>{data.after?.count ?? 0}</strong>
      </p>

      {(data.after?.added || []).length > 0 && (
        <div className="flex flex-wrap gap-1" data-testid="role-preview-added">
          {data.after.added.slice(0, 14).map((cap) => (
            <Badge key={cap} variant="secondary" className="text-[10px]">+ {cap}</Badge>
          ))}
          {data.after.added.length > 14 && (
            <Badge variant="outline" className="text-[10px]">
              +{data.after.added.length - 14} more
            </Badge>
          )}
        </div>
      )}

      <p className="text-xs text-slate-600" data-testid="role-preview-units">
        <Building2 className="mr-1 inline h-3 w-3" />
        {data.units?.all
          ? 'Covers every unit on the platform'
          : data.units?.count
            ? `Covers ${data.units.count} unit${data.units.count === 1 ? '' : 's'}${
                data.units.names?.length ? `: ${data.units.names.slice(0, 4).join(', ')}` : ''}`
            : 'Covers no unit yet — attach them to a church or province'}
      </p>

      {(data.appointments || []).length > 0 && (
        <div className="text-xs text-slate-600" data-testid="role-preview-appointments">
          <span className="font-medium">Already appointed:</span>
          <ul className="mt-1 space-y-0.5">
            {data.appointments.slice(0, 5).map((a) => (
              <li key={`${a.source}-${a.office}-${a.unit_id}`} className="flex items-center gap-1">
                <Check className="h-3 w-3 text-emerald-600" />
                {(a.title || a.office).replace(/_/g, ' ')}
                {a.unit_name ? ` · ${a.unit_name}` : ''}
                <span className="text-slate-400">({a.source.replace(/_/g, ' ')})</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {(data.committee_seats || []).length > 0 && (
        <p className="text-xs text-slate-600" data-testid="role-preview-seats">
          Holds {data.committee_seats.length} committee seat
          {data.committee_seats.length === 1 ? '' : 's'} —
          {data.committee_seats.some((s) => s.manages)
            ? ' may manage its membership'
            : ' read-only on its records'}
        </p>
      )}
    </div>
  );
};

// Assign Role Dialog Component
const AssignRoleDialog = ({ open, onClose, user, users, roles, defaultRoles, orgUnits, onAssign, isLoading }) => {
  const [selectedUserId, setSelectedUserId] = useState(user?.id || '');
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [selectedOrgUnitId, setSelectedOrgUnitId] = useState('');

  React.useEffect(() => {
    if (user) {
      setSelectedUserId(user.id);
    }
  }, [user]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedUserId || !selectedRoleId) {
      toast.error('Please select a user and role');
      return;
    }
    onAssign({
      userId: selectedUserId,
      roleId: selectedRoleId,
      orgUnitId: selectedOrgUnitId || null
    });
  };

  // Ensure roles is an array
  const rolesArray = Array.isArray(roles) ? roles : [];
  const usersArray = Array.isArray(users) ? users : [];
  const orgUnitsArray = Array.isArray(orgUnits) ? orgUnits : [];

  // Combine system and custom roles
  const allRoles = [
    ...(defaultRoles ? Object.keys(defaultRoles).map(name => ({
      id: `system_${name}`,
      name: name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      type: 'system',
      ...defaultRoles[name]
    })) : []),
    ...rolesArray.filter(r => !r.is_system_role).map(r => ({ ...r, type: 'custom' }))
  ];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Assign Role to User</DialogTitle>
          <DialogDescription>
            Select a user, role, and optionally scope it to a specific organization unit
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>User</Label>
            <Select value={selectedUserId} onValueChange={setSelectedUserId}>
              <SelectTrigger data-testid="select-user">
                <SelectValue placeholder="Select user" />
              </SelectTrigger>
              <SelectContent>
                {usersArray.map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.first_name} {u.last_name} ({u.email})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Role</Label>
            <Select value={selectedRoleId} onValueChange={setSelectedRoleId}>
              <SelectTrigger data-testid="select-role">
                <SelectValue placeholder="Select role" />
              </SelectTrigger>
              <SelectContent>
                <div className="px-2 py-1 text-xs font-semibold text-slate-500">System Roles</div>
                {allRoles.filter(r => r.type === 'system').map((role) => (
                  <SelectItem key={role.id} value={role.id}>
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${ROLE_COLORS[role.name?.toLowerCase().replace(/ /g, '_')] || 'bg-gray-500'}`} />
                      {role.name}
                      {role.level && <Badge variant="outline" className="ml-1 text-xs">{role.level}</Badge>}
                    </div>
                  </SelectItem>
                ))}
                {allRoles.filter(r => r.type === 'custom').length > 0 && (
                  <>
                    <div className="px-2 py-1 text-xs font-semibold text-slate-500 mt-2">Custom Roles</div>
                    {allRoles.filter(r => r.type === 'custom').map((role) => (
                      <SelectItem key={role.id} value={role.id}>
                        {role.name}
                      </SelectItem>
                    ))}
                  </>
                )}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Scope to Organization Unit (Optional)</Label>
            <Select value={selectedOrgUnitId || 'global'} onValueChange={(v) => setSelectedOrgUnitId(v === 'global' ? '' : v)}>
              <SelectTrigger data-testid="select-org-unit">
                <SelectValue placeholder="All units (global role)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="global">Global (all accessible units)</SelectItem>
                {orgUnitsArray.map((unit) => (
                  <SelectItem key={unit.id} value={unit.id}>
                    <div className="flex items-center gap-2">
                      <Badge className={LEVEL_BADGES[unit.level]?.color || 'bg-gray-100'} variant="outline">
                        {LEVEL_BADGES[unit.level]?.label || unit.level}
                      </Badge>
                      {unit.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500 mt-1">
              Leave empty for global access, or select a unit to limit their access
            </p>
          </div>

          <RolePreview userId={selectedUserId} roleId={selectedRoleId} />

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={isLoading || !selectedUserId || !selectedRoleId}>
              {isLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Assign Role
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Privacy Settings Dialog Component
const PrivacySettingsDialog = ({ open, onClose, unit, onSave, isLoading }) => {
  const [settings, setSettings] = useState({
    donor_visibility: 'own_level',
    member_visibility: 'own_level',
    finance_visibility: 'own_level',
    aggregation_above: true,
  });

  // Fetch current settings
  const { data: currentSettings } = useQuery({
    queryKey: ['privacy-settings', unit?.id],
    queryFn: async () => {
      if (!unit?.id) return null;
      const token = getToken();
      const res = await fetch(`${API_URL}/api/admin/rbac/privacy/${unit.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return res.json();
    },
    enabled: !!unit?.id && open
  });

  React.useEffect(() => {
    if (currentSettings) {
      setSettings({
        donor_visibility: currentSettings.donor_visibility || 'own_level',
        member_visibility: currentSettings.member_visibility || 'own_level',
        finance_visibility: currentSettings.finance_visibility || 'own_level',
        aggregation_above: currentSettings.aggregation_above !== false,
      });
    }
  }, [currentSettings]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ unitId: unit.id, settings });
  };

  const visibilityOptions = [
    { value: 'own_level', label: 'Own Level Only', description: 'Only visible to this unit' },
    { value: 'anonymized', label: 'Anonymized Above', description: 'Names hidden at parent levels' },
    { value: 'hidden', label: 'Completely Hidden', description: 'Not visible above this unit' },
  ];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Privacy Settings: {unit?.name}</DialogTitle>
          <DialogDescription>
            Configure how data from this unit is visible to parent levels
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <div>
              <Label className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Donor Visibility
              </Label>
              <Select 
                value={settings.donor_visibility} 
                onValueChange={(v) => setSettings({...settings, donor_visibility: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {visibilityOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      <div>
                        <div className="font-medium">{opt.label}</div>
                        <div className="text-xs text-slate-500">{opt.description}</div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Member Visibility
              </Label>
              <Select 
                value={settings.member_visibility} 
                onValueChange={(v) => setSettings({...settings, member_visibility: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {visibilityOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      <div>
                        <div className="font-medium">{opt.label}</div>
                        <div className="text-xs text-slate-500">{opt.description}</div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Finance Visibility
              </Label>
              <Select 
                value={settings.finance_visibility} 
                onValueChange={(v) => setSettings({...settings, finance_visibility: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {visibilityOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      <div>
                        <div className="font-medium">{opt.label}</div>
                        <div className="text-xs text-slate-500">{opt.description}</div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <Label>Show Aggregated Data to Parent Levels</Label>
                <p className="text-sm text-slate-500">
                  Allow parent units to see totals without individual details
                </p>
              </div>
              <Switch
                checked={settings.aggregation_above}
                onCheckedChange={(v) => setSettings({...settings, aggregation_above: v})}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Settings
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default HierarchicalRoleAssignment;
